/**
 * useAutoClassifyOnce — hook auto-classify one-shot (Chunk 9.T.4).
 *
 * Extraction ciblée depuis `IntakeEventDetailPage` pour permettre une
 * couverture RTL isolée non-flaky (le test full-page hang le worker vitest
 * jsdom via une interaction useSearchParams + useEffect + useMutation
 * difficile à isoler proprement).
 *
 * Un seul call-site : `IntakeEventDetailPage`. Zéro autre consommateur. Pas
 * de surarchitecture — c'est bien un hook custom cohérent (gère un effet
 * cross-cutting avec guard ref one-shot), pas un helper de testabilité
 * artificiel.
 *
 * Comportement :
 *   - Déclenche `onTrigger` une fois quand TOUTES les conditions sont vraies :
 *       eventId présent + `?auto_classify=1` dans l'URL + event en statut
 *       `pending_classification` + pas de classification déjà en payload +
 *       pas de mutation en vol + jamais déjà déclenché ce mount.
 *   - Clear le search param immédiatement via `onClearSearchParam` — un F5
 *     manuel du broker ne re-déclenche donc PAS l'auto-trigger.
 *   - Guard `useRef` → une seule invocation par cycle de vie du composant
 *     (survit aux re-renders StrictMode double-effect).
 *
 * Le hook ne réalise PAS d'appel réseau lui-même — il compose la
 * décision + délègue `onTrigger` au call-site (qui a le vrai `mutate`). Ça
 * garde le hook pur (aucune dépendance à React Query, à l'API, aux
 * search params). Testable via `renderHook` avec des callbacks stubs.
 */

import { useEffect, useRef } from "react";

export type AutoClassifyOnceOptions = {
  /** UUID de l'intake_event. `undefined` = URL sans param → jamais trigger. */
  readonly eventId: string | undefined;
  /** Statut courant de l'event. Trigger uniquement si `pending_classification`. */
  readonly status: string | undefined;
  /** `true` si `event.payload.classification` déjà présent. Bloque le trigger. */
  readonly hasClassification: boolean;
  /** `true` si la mutation classify est actuellement en vol. Bloque le trigger. */
  readonly isClassifying: boolean;
  /** `true` si `?auto_classify=1` présent dans l'URL. Requis pour trigger. */
  readonly shouldAutoClassify: boolean;
  /**
   * Callback pour clear le search param `?auto_classify=1` — appelé avant
   * `onTrigger` pour garantir qu'un F5 rechargement ne re-déclenche pas.
   */
  readonly onClearSearchParam: () => void;
  /** Callback appelé pour déclencher effectivement la classification. */
  readonly onTrigger: () => void;
};

/**
 * Retourne vrai si toutes les conditions sont satisfaites pour déclencher
 * une auto-classification. Fonction pure — extraite du hook pour permettre
 * une couverture test additionnelle si nécessaire (edge cases sans overhead
 * `renderHook`).
 */
export function computeAutoClassifyEligible(opts: {
  readonly eventId: string | undefined;
  readonly status: string | undefined;
  readonly hasClassification: boolean;
  readonly isClassifying: boolean;
  readonly shouldAutoClassify: boolean;
  readonly alreadyTriggered: boolean;
}): boolean {
  return (
    !!opts.eventId &&
    !opts.alreadyTriggered &&
    opts.shouldAutoClassify &&
    opts.status === "pending_classification" &&
    !opts.hasClassification &&
    !opts.isClassifying
  );
}

export function useAutoClassifyOnce(opts: AutoClassifyOnceOptions): void {
  const alreadyTriggeredRef = useRef(false);

  useEffect(() => {
    const eligible = computeAutoClassifyEligible({
      eventId: opts.eventId,
      status: opts.status,
      hasClassification: opts.hasClassification,
      isClassifying: opts.isClassifying,
      shouldAutoClassify: opts.shouldAutoClassify,
      alreadyTriggered: alreadyTriggeredRef.current,
    });
    if (eligible) {
      alreadyTriggeredRef.current = true;
      opts.onClearSearchParam();
      opts.onTrigger();
    }
    // NOTE : deps volontairement `opts.*` primitifs — les 2 callbacks
    // (`onClearSearchParam` / `onTrigger`) sont exclus pour éviter un
    // re-fire à chaque render si le call-site ne les mémoïse pas. Le
    // guard `alreadyTriggeredRef` protège l'invariance one-shot même en
    // cas de re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    opts.eventId,
    opts.status,
    opts.hasClassification,
    opts.isClassifying,
    opts.shouldAutoClassify,
  ]);
}
